<template>
  <div class="game-offer">
    <div class="game-offer__content container">
      <div class="game-offer__info">
        <offer-advantages class="game-offer__advantages" />

        <div class="game-offer__info-head">
          <div
            v-if="bullits.length"
            class="game-offer__bullits">
            <div
              v-for="(bullit, index) in bullits"
              :key="index"
              class="game-offer__bullit">
              {{ bullit }}
            </div>
          </div>
          <div
            v-if="monthlySubscription"
            class="game-offer__subscription">
            <span class="game-offer__subscription-title">4 in 1</span>
            <span class="game-offer__subscription-text">Monthly<br>Subscription</span>
          </div>
        </div>

        <div class="game-offer__image-wrap">
          <responsive-image
            v-if="hasProductData && product.picture_item"
            :image-data="{
              default: productDetailImage.small || productDetailImage.large,
              fallback: '/images/games/picture_item.png',
              sources: [
                {
                  media: '(-webkit-min-device-pixel-ratio: 2)',
                  srcset: productDetailImage.large,
                },
              ],
            }"
            class="game-offer__image" />
        </div>

        <div class="game-offer__timings">
          <transition name="transition--opacity">
            <div
              v-if="hasProductData && product.meta && product.meta.estimatedStartTime"
              class="game-offer__timing">
              <div class="game-offer__timing-value">
                {{ product.meta.estimatedStartTime }}
              </div>

              <div class="game-offer__timing-title">
                Estimated Start Time
              </div>
            </div>
          </transition>

          <transition name="transition--opacity">
            <div
              v-if="hasProductData && (executionTime || product.meta.estimatedCompletionsTime)"
              class="game-offer__timing">
              <text-date
                v-if="executionTime"
                class="game-offer__timing-value"
                :value="executionTime"
                :use-seconds="true"
                :is-editable="false" />

              <div
                v-else
                class="game-offer__timing-value">
                {{ product.meta.estimatedCompletionsTime }}
              </div>

              <div class="game-offer__timing-title">
                Estimated Completion Time
              </div>
            </div>
          </transition>
        </div>
      </div>

      <div class="game-offer__order">
        <div
          v-if="availablePlatformsStr"
          class="game-offer__platforms">
          Available on {{ availablePlatformsStr }}
        </div>
        <product-order-area
          v-if="hasProductData"
          ref="orderArea"
          :display-price.sync="displayPrice"
          class="game-offer__order-area"
          :product="product"
          @execution-time="handleTotalExecutionTimeUpdate" />
      </div>

      <transition name="transition--opacity">
        <div
          v-if="hasProductData && !product._shouldActualize"
          class="game-offer__tabs">
          <product-description
            show-navigation
            :product="product" />
        </div>
      </transition>
    </div>

    <base-card-list
      v-if="relatedProducts.length"
      :scrollable-from="1023"
      :items="relatedProducts"
      class="game-offer__related container">
      <template #title>
        You may also like
      </template>

      <template #card="{ item }">
        <product-card
          :product="item"
          :show-card-footer="userDevice.innerWidth >= 1024" />
      </template>
    </base-card-list>

    <how-it-works class="game-offer__how container" />

    <we-try-hard class="game-offer__we-try-hard" />

    <breadcrumbs
      class="container game-offer__breadcrumbs"
      :enable-schema="false"
      :items="breadcrumbs" />

    <div
      v-if="showAdditionalOrderBtn"
      class="additional-buy-btn-wrapper">
      <button
        class="button button-main additional-buy-btn"
        @click="scrollToOrderArea">
        <img
          data-src="/images/icons/shield.svg"
          class="lazyload additional-buy-btn__img"
          width="14"
          height="14"
          alt="">
        Buy now for {{ currencyValue(displayPrice) }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import GameModel from 'Models/GameModel';
import { scrollIntoView } from 'Root/helpers/tools';
import tinymceComponents from 'Root/mixins/tinymceComponents';
import ProductOrderArea from 'Components/OrderArea/ProductOrderArea.vue';
import Breadcrumbs from 'Components/ui/Breadcrumbs/Breadcrumbs.vue';
import ResponsiveImage from 'Components/ui/Image/ResponsiveImage.vue';
import BaseCardList from 'Components/ui/BaseCardList/BaseCardList.vue';
import HowItWorks from 'Root/pages/game/components/HowItWorks.vue';
import WeTryHard from 'Root/pages/game/components/WeTryHard.vue';
import ProductDescription from 'Root/pages/game/components/ProductDescription.vue';
import ProductCard from 'Root/pages/game/components/ProductCard.vue';
import OfferAdvantages from 'Root/pages/game/components/OfferAdvantages.vue';
import TextDate from 'Components/TextDate.vue';

export default {
  name: 'GameOfferDefault',

  components: {
    OfferAdvantages,
    ResponsiveImage,
    Breadcrumbs,
    ProductOrderArea,
    HowItWorks,
    WeTryHard,
    ProductDescription,
    BaseCardList,
    ProductCard,
    TextDate,
  },

  mixins: [
    tinymceComponents,
  ],

  data() {
    return {
      showAdditionalOrderBtn: false,
      scrollPosition: 0,
      displayPrice: 0,
      executionTime: 0,
      buyButtonIntersectionObserver: null,
    };
  },

  computed: {
    ...mapState(['activeRegion', 'userDevice']),
    ...mapState('products', ['product']),
    ...mapGetters(['currencyValue']),
    ...mapGetters('products', ['breadcrumbs']),

    hasProductData() {
      return this.product?.id;
    },

    bullits() {
      return this.product?.advantages?.filter((a) => a) || [];
    },

    monthlySubscription() {
      return this.product.meta?.isMonthlySubscription;
    },

    relatedProducts() {
      return (this.product?.related_products || [])
        .filter((p) => p.available_in_regions.includes(this.activeRegion.id));
    },

    availablePlatformsStr() {
      if (!this.hasProductData) {
        return false;
      }

      const { platforms } = GameModel.ID_TO_DATA[+this.product.game];

      if (!platforms) {
        return false;
      }

      return platforms.map((p) => p.title).join(', ');
    },

    productDetailImage() {
      return {
        small: this.product?.picture_item_small ? `/storage${this.product.picture_item_small}` : null,
        large: this.product?.picture_item ? `/storage${this.product.picture_item}` : null,
      };
    },
  },

  watch: {
    'product.id': {
      handler() {
        this.initBuyButtonObserver();
      },
    },
  },

  async mounted() {
    // 32 = высота кнопки купить
    this.initBuyButtonObserver();
  },

  beforeDestroy() {
    if (this.buyButtonIntersectionObserver) {
      this.buyButtonIntersectionObserver.disconnect();
    }
  },

  // beforeRouteLeave(to, from, next) {
  // todo SKY-1577 при заказе на постоплате хотели сохранять фон купленного товара
  // this.mutateBackgrounds({ type: MutationType.POP });
  // this.$store.dispatch('setDefaultBackground');
  // next();
  // },

  methods: {
    // showProductsDisabledPopup() {
    //   if (['462', '457'].includes(this.productId)
    //      && !popupShowed
    //      && this.$store.state.activeRegion.id === RegionModel.USA.id) {
    //     popupShowed = true;
    //     import('Components/Popups/ProductsDisabledPopup')
    //       .then((module) => {
    //         this.$vuedals.open({
    //           name: 'products-disabled',
    //           component: module.default,
    //           size: 'sm',
    //         });
    //         popupShowed = false;
    //       });
    //   }
    // },

    initBuyButtonObserver() {
      setTimeout(() => {
        const buyBtn = document.querySelector('.order-area__buy-now');
        this.buyButtonIntersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.showAdditionalOrderBtn = this.$store.state.userDevice.innerWidth < 1024
                    && !entry.isIntersecting;
          });
        }, {
          threshold: 1,
          rootMargin: '32px 0px 0px 0px',
        });
        if (buyBtn) {
          this.buyButtonIntersectionObserver.observe(buyBtn);
        }
      }, 0);
    },

    scrollToOrderArea() {
      const orderArea = document.querySelector('.order-area__wrapper');
      scrollIntoView(orderArea);
    },

    handleTotalExecutionTimeUpdate(totalExecutionTime) {
      this.executionTime = totalExecutionTime;
    },
  },
};
</script>

<style lang="scss">
  .responsive-image.offer-bg-img {
    &:after {
      z-index: 1;
    }

    .responsive-image__img {
      object-fit: cover;
      height: 100%;
    }
  }

  .responsive-image.offer-bg-img--transparent .responsive-image__img {
    opacity: 0.35;
  }

  .additional-buy-btn-wrapper {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100vw;
    padding: 20px;
    background-color: rgba($color--additional-gray, .95);
    z-index: 1000;
  }

  .additional-buy-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 10px;
    width: 100%;
  }

  .additional-buy-btn__img {
    margin-right: 9px;
    height: auto;
  }
</style>

<style lang="scss" scoped>
  .game-offer .game-offer__related {
    --item-width: 250px;
  }

  .game-offer__content {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 15px;
  }

  .game-offer__bullits {
    display: grid;
    grid-template-rows: repeat(3, max-content);
    grid-template-columns: max-content;
    grid-gap: 3px;
    z-index: 3;
    position: relative;
    margin-bottom: 35px;
  }

  .game-offer__bullit {
    padding: 7px 35px 7px 7px;
    font-weight: 400;
    font-size: 11px;
    background: linear-gradient(90deg, #5F73FA 0%, rgba(95, 115, 250, 0) 92.34%);
  }

  .game-offer__info {
    padding: 0 var(--padding);
    display: grid;
    grid-template-columns: 50% 50%;
  }

  .game-offer__image-wrap {
    position: relative;
    z-index: 2;
  }

  .game-offer__image {
    position: absolute;
    right: calc(-1 * var(--padding));
    top: 55%;
    transform: translateY(-50%);
    max-width: 250px;
    max-height: 250px;
    width: calc(100% + var(--padding) * 2);
    overflow: hidden;
  }

  .game-offer__timings {
    position: relative;
    z-index: 3;
    display: grid;
    grid-template-columns: repeat(2, max-content);
    grid-gap: 20px;
    font-weight: 500;
    grid-column: 1 / span 2;
    grid-row: 2;
  }

  .game-offer__timing-value {
    font-size: 21px;
    margin-bottom: 8px;
  }

  /deep/.text-date > .text-date__info-wrapper > .text-date__info {
    font-size: 21px;
  }

  .game-offer__timing-title {
    font-size: 11px;
  }

  .game-offer__tabs {
    min-width: 0; // Чтобы слайдер не переполнял родителя
  }

  .game-offer__order-area {
    align-self: flex-start;
    position: relative;
    z-index: 4;
    background-color: $color--basic-black;
  }

  .game-offer__related {
    padding-top: 40px;
    background-color: $color--basic-black;
  }

  .game-offer__we-try-hard {
    padding: 25px 0;
  }

  .game-offer__how {
    margin-bottom: 0;
    padding-top: 40px;
    background-color: $color--basic-black;
    padding-bottom: 0;
  }

  .game-offer__platforms {
    font-size: 13px;
    font-weight: 500;
    color: $color--basic-dark-gray;
    margin-bottom: 14px;
    position: relative;
    z-index: 2;
  }

  .game-offer__breadcrumbs {
    margin-bottom: 5px;
  }

  .game-offer__info-head {
    display: flex;
  }

  .game-offer__subscription {
    background: url(/images/icons/star-green.svg);
    background-size: cover;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 500;
  }

  .game-offer__advantages {
    order: 3;
    grid-column: 1 / -1;
    z-index: 3;
    margin-top: 16px;
  }

  @media screen and (max-width: 719px) {
    .game-offer__subscription {
      bottom: 100%;
      transform: translateY(33%);
    }

    .game-offer__advantages {
      margin-left: calc(-1 * var(--padding));
      margin-right: calc(-1 * var(--padding));
      padding: 0 var(--padding);
      scroll-padding-left: var(--padding);
    }
  }

  @media screen and (max-width: 839px) {
    .game-offer__subscription {
      width: 55px;
      height: 55px;
      position: absolute;
      bottom: 160%;
      transform: translateY(80%);
      right: var(--padding);
    }

    .game-offer__subscription-text {
      display: none;
    }
  }

  @media screen and (min-width: 720px) {
    .game-offer__info {
      position: relative;
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .game-offer__image-wrap {
      position: static;
      z-index: 0; // чтобы тень от фона перекрывала картинку
    }

    .game-offer__image {
      width: 40%;
      max-width: 280px;
      max-height: 280px;
      height: auto;
      bottom: 0;
      top: auto;
      right: var(--padding);
      transform: none;
    }

    .game-offer__order-area {
      padding: 25px;
      box-shadow: 0 4px 15px rgba(95, 115, 250, 0.15);
      background-color: $color--basic-black;
      position: sticky;
      top: 20px;
    }

    .game-offer__bullits {
      grid-gap: 5px;
      margin-bottom: 15px;
    }

    .game-offer__bullit {
      font-size: 19px;
      padding: 9px 45px 9px 15px;
    }

    .game-offer__timing-value {
      font-size: 35px;
      margin-bottom: 15px;
    }

    /deep/.text-date > .text-date__info-wrapper > .text-date__info {
      font-size: 35px;
    }

    .game-offer__timing-title {
      font-size: 13px;
    }

    .game-offer__platforms {
      font-size: 19px;
    }
  }

  @media screen and (min-width: 840px) {
    .game-offer__info-head {
      max-width: 50%;
      width: 100%;
      justify-content: space-between;
    }

    .game-offer__subscription {
      width: 130px;
      height: 130px;
      z-index: 3;
      position: relative;
    }

    .game-offer__subscription-title {
      font-size: 25px;
      margin-bottom: 2px;
    }
  }

  @media screen and #{$screen-more-or-1024} {
    .game-offer__we-try-hard {
      padding: 65px 0 25px;
    }

    .game-offer__image {
      right: 8%;
    }
  }

  @media screen and #{$screen-less-than-1280} {
    .game-offer__content {
      padding: 0;
    }

    .game-offer__order {
      position: relative;
      background-color: $color--basic-black;
      padding: 0 var(--padding);

      &:before {
        content: '';
        position: absolute;
        bottom: calc(100% - 20px);
        left: 0;
        width: 100%;
        height: 100px;
        background: linear-gradient(180deg, rgba($color--basic-black, 0) 0%, $color--basic-black 100%);
        z-index: 2;
      }
    }
  }

  @media screen and #{$screen-more-or-1280} {
    // на загрузке пока блок с отзывами не отображается попадает под градиент
    .game-offer__how {
      position: relative;
      z-index: 2;
    }

    .game-offer__advantages {
      order: unset;
      margin-top: 0;
    }

    .game-offer__image {
      position: absolute;
      bottom: -13%;
      z-index: 0;
      height: auto;
      max-height: 450px;
      width: 100%;
      max-width: 450px;
      left: 47%;
      transform: translateX(-50%);
      right: auto;
    }

    .game-offer__tabs {
      grid-row: 2;
      grid-column: 1;
      z-index: 2;
      padding: 0;
    }

    .game-offer__info {
      padding: 0;
      min-height: 450px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .game-offer__content {
      grid-gap: 60px 20px;
      grid-template-columns: 3fr minmax(600px, 2fr);
    }

    .game-offer__order {
      grid-column: 2;
      grid-row: 1 / span 2;
    }

    .game-offer__platforms {
      text-align: right;
    }

    .game-offer__timings {
      grid-gap: 30px;
    }

    .game-offer__info-head {
      max-width: 100%;
    }
  }

  @media screen and (min-width: 1400px) {
    .game-offer__image {
      max-height: 550px;
      max-width: 550px;
    }
  }
</style>
